<template>
	<v-main>
		<v-container fluid>
			<FormPage :title="isEdit ? 'Modifica costo' : 'Nuovo costo'" :subtitle="form.category" :back-label="backLabel" save-label="Salva" :save-disabled="saving" @back="back" @save="salva">
				<v-form ref="form" @submit.prevent="salva">
					<v-row dense>
						<v-col cols="12" md="7">
							<SectionCard title="Voce di costo" icon="mdi-receipt-text-outline">
								<v-row dense>
									<v-col cols="12" sm="6">
										<v-text-field v-model="form.month" type="month" label="Mese di competenza" outlined dense :rules="[required]" />
									</v-col>
									<v-col cols="12" sm="6">
										<v-combobox v-model="form.category" :items="categories" label="Categoria" outlined dense :rules="[required]" hint="Si può scriverne una nuova" />
									</v-col>
									<v-col cols="12">
										<v-text-field v-model="form.description" label="Descrizione" outlined dense counter="500" />
									</v-col>
									<v-col cols="12">
										<v-text-field v-model="form.counterpart" label="Fornitore o dipendente" outlined dense hint="Facoltativo" persistent-hint />
									</v-col>
									<!-- pubblicita': la piattaforma evita di contare due volte la spesa letta dalle API (Report -> Pubblicita') -->
									<v-col v-if="isAds" cols="12">
										<v-select
											v-model="form.adSource"
											:items="AD_SOURCES"
											label="Piattaforma pubblicitaria"
											outlined
											dense
											clearable
											hint="Se la spesa di questa piattaforma è già letta in automatico, nei mesi con il dato letto questo costo non si somma"
											persistent-hint
										/>
									</v-col>
								</v-row>
							</SectionCard>

							<SectionCard title="Ricorrenza" icon="mdi-repeat" class="mt-2">
								<v-switch v-model="form.recurring.monthly" color="primary" inset hide-details label="Si ripete ogni mese (es. stipendio, affitto, abbonamento)" class="mt-0" />
								<v-text-field
									v-if="form.recurring.monthly"
									v-model="form.recurring.until"
									type="month"
									label="Fino al mese (vuoto = senza scadenza)"
									outlined
									dense
									clearable
									class="mt-4 cf-until"
									:rules="[untilRule]"
								/>
							</SectionCard>
						</v-col>

						<v-col cols="12" md="5">
							<SectionCard title="Importo e IVA" icon="mdi-percent-outline">
								<v-switch v-model="ivaInclusa" color="primary" inset hide-details :label="ivaInclusa ? 'Importo IVA inclusa' : 'Importo IVA esclusa'" class="mt-0 mb-4" />
								<v-text-field v-model="form.amount" type="number" step="0.01" :label="ivaInclusa ? 'Totale (IVA inclusa)' : 'Imponibile'" prefix="€" outlined dense :rules="[required, numberRule]" />
								<v-select v-model="form.vatRate" :items="rateItems" label="Aliquota IVA" outlined dense />
								<v-select
									v-if="+form.vatRate === 0"
									v-model="form.vatNature"
									:items="VAT_NATURES"
									label="Natura dell'operazione"
									outlined
									dense
									hint="Per i costi del personale: fuori campo IVA"
									persistent-hint
								/>
								<v-text-field
									v-if="+form.vatRate > 0"
									v-model="form.vatNonDeductiblePct"
									type="number"
									min="0"
									max="100"
									label="IVA indetraibile"
									suffix="%"
									outlined
									dense
									hint="Es. 60 per le auto aziendali (detraibile al 40%). La parte indetraibile diventa costo."
									persistent-hint
									:rules="[pctRule]"
								/>

								<div class="cf-preview mt-4">
									<div class="cf-muted">Imponibile</div>
									<div class="text-right">{{ formatEurCents(preview.net) }}</div>
									<div class="cf-muted">IVA {{ +form.vatRate > 0 ? form.vatRate + '%' : '' }}</div>
									<div class="text-right">{{ formatEurCents(preview.vat) }}</div>
									<template v-if="preview.nonDeductible">
										<div class="cf-muted">di cui indetraibile</div>
										<div class="text-right">{{ formatEurCents(preview.nonDeductible) }}</div>
									</template>
									<div class="cf-strong">Totale</div>
									<div class="text-right cf-strong">{{ formatEurCents(preview.gross) }}</div>
									<div class="cf-strong">Costo nel conto economico</div>
									<div class="text-right cf-strong">{{ formatEurCents(preview.net + preview.nonDeductible) }}</div>
								</div>
							</SectionCard>
						</v-col>
					</v-row>
				</v-form>
			</FormPage>
		</v-container>
	</v-main>
</template>

<script>
import { mapActions } from 'vuex';
import FormPage from '@/components/core/FormPage.vue';
import SectionCard from '@/components/core/SectionCard.vue';
import { childPage } from '@/components/core/childPage';
import { formatEurCents, previousMonth, VAT_RATES, VAT_NATURES } from '@/components/finance/format';

const round2 = n => Math.round(((+n || 0) + Number.EPSILON) * 100) / 100;

const emptyForm = () => ({
	month: previousMonth(),
	category: '',
	description: '',
	counterpart: '',
	amount: '',
	vatRate: 22,
	vatNature: 'fuori_campo',
	vatNonDeductiblePct: 0,
	recurring: { monthly: false, until: null },
	adSource: null,
});

// piattaforme della categoria Pubblicita' (features/ads): stessa chiave del backend
const AD_SOURCES = [
	{ value: 'google', text: 'Google Ads' },
	{ value: 'meta', text: 'Meta (Facebook, Instagram)' },
	{ value: 'tiktok', text: 'TikTok Ads' },
	{ value: 'microsoft', text: 'Microsoft Ads' },
];

export default {
	name: 'FinanceCostForm',
	mixins: [childPage({ listPath: '/finance/costs', kind: 'financeCost' })],
	components: { FormPage, SectionCard },
	data() {
		return {
			form: emptyForm(),
			ivaInclusa: true,
			categories: [],
			saving: false,
			AD_SOURCES,
			VAT_NATURES,
			rateItems: VAT_RATES.map(r => ({ value: r, text: r ? `${r}%` : '0% (esente, fuori campo, ...)' })),
		};
	},
	computed: {
		isEdit() {
			return !!this.$route.params.id;
		},
		isAds() {
			return /^pubblicit/i.test(String(this.form.category || ''));
		},
		// stesso calcolo del server (features/finance/service.js normalizeCost): si salva quello che si vede
		preview() {
			const amount = +this.form.amount || 0;
			const rate = +this.form.vatRate || 0;
			let net;
			let vat;
			if (this.ivaInclusa) {
				net = round2(amount / (1 + rate / 100));
				vat = round2(amount - net);
			} else {
				net = round2(amount);
				vat = round2((amount * rate) / 100);
			}
			const pct = rate > 0 ? +this.form.vatNonDeductiblePct || 0 : 0;
			return { net, vat, gross: round2(net + vat), nonDeductible: round2((vat * pct) / 100) };
		},
	},
	async mounted() {
		try {
			const res = await this.fetchSettings();
			this.categories = (res.settings && res.settings.categories) || [];
			if (this.isEdit) {
				const doc = await this.fetchCost(this.$route.params.id);
				this.ivaInclusa = doc.enteredAs !== 'net';
				this.form = {
					month: doc.month,
					category: doc.category,
					description: doc.description || '',
					counterpart: doc.counterpart || '',
					amount: this.ivaInclusa ? doc.gross : doc.net,
					vatRate: doc.vatRate,
					vatNature: doc.vatNature || 'fuori_campo',
					vatNonDeductiblePct: doc.vatNonDeductiblePct || 0,
					recurring: { monthly: !!(doc.recurring && doc.recurring.monthly), until: (doc.recurring && doc.recurring.until) || null },
					adSource: doc.adSource || null,
				};
			}
		} catch (err) {
			if (err.response && err.response.status === 404) return this.notFound('Costo non trovato');
			this.notify('error', (err.response && err.response.data && err.response.data.message) || err.message);
		}
	},
	methods: {
		...mapActions({ fetchCost: 'finance/fetchCost', saveCost: 'finance/saveCost', fetchSettings: 'finance/fetchSettings' }),
		formatEurCents,
		required: v => (v !== '' && v !== null && v !== undefined) || 'Campo obbligatorio',
		numberRule: v => !Number.isNaN(parseFloat(v)) || 'Importo non valido',
		pctRule: v => (+v >= 0 && +v <= 100) || 'Tra 0 e 100',
		untilRule(v) {
			return !v || v >= this.form.month || 'Deve essere uguale o successivo al mese di competenza';
		},
		async salva() {
			if (!this.$refs.form.validate()) return;
			this.saving = true;
			const rate = +this.form.vatRate || 0;
			const data = {
				month: this.form.month,
				category: String(this.form.category || '').trim(),
				description: this.form.description,
				counterpart: this.form.counterpart,
				amount: +this.form.amount,
				enteredAs: this.ivaInclusa ? 'gross' : 'net',
				vatRate: rate,
				vatNature: rate > 0 ? null : this.form.vatNature,
				vatNonDeductiblePct: rate > 0 ? +this.form.vatNonDeductiblePct || 0 : 0,
				recurring: { monthly: !!this.form.recurring.monthly, until: this.form.recurring.monthly ? this.form.recurring.until || null : null },
				adSource: this.isAds ? this.form.adSource || null : null,
			};
			try {
				const doc = await this.saveCost({ id: this.$route.params.id, data });
				this.saved(this.isEdit ? 'Costo aggiornato' : 'Costo inserito', doc);
			} catch (err) {
				const errors = err.response && err.response.data && err.response.data.error;
				this.notify('error', Array.isArray(errors) ? errors.map(e => `${e.param}: ${e.msg}`).join(', ') : err.message);
			} finally {
				this.saving = false;
			}
		},
	},
};
</script>

<style scoped>
.cf-until {
	max-width: 320px;
}
.cf-preview {
	display: grid;
	grid-template-columns: 1fr auto;
	row-gap: 4px;
	column-gap: 12px;
	padding: 10px 12px;
	border-radius: 8px;
	background: #f7f9fc;
	font-size: 14px;
}
.cf-muted {
	color: #5f6b7a;
}
.cf-strong {
	font-weight: 600;
}
</style>
